/**
 * Silently authenticates on load — no login page shown.
 * On first run it creates the owner account; on subsequent runs it signs in.
 */
import { useEffect, useState, type ReactNode } from 'react';
import { useAuth } from '../../hooks/useAuth.js';

const OWNER_EMAIL = 'owner@locationbreaker.local';
const OWNER_PASSWORD = 'Owner1234!';

export function AutoAuth({ children }: { children: ReactNode }) {
  const { isAuthenticated, signIn, signUp } = useAuth();
  const [ready, setReady] = useState(isAuthenticated);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isAuthenticated) { setReady(true); return; }

    let cancelled = false;

    async function tryAuth(attemptsLeft: number) {
      if (cancelled) return;

      const login = await signIn(OWNER_EMAIL, OWNER_PASSWORD);
      if (login.success) { if (!cancelled) setReady(true); return; }

      const reg = await signUp(OWNER_EMAIL, OWNER_PASSWORD, 'Owner', 'Account');
      if (reg.success) { if (!cancelled) setReady(true); return; }

      if (attemptsLeft > 1) {
        setTimeout(() => tryAuth(attemptsLeft - 1), 2000);
      } else if (!cancelled) {
        setError('Could not connect to the API. Is the Wrangler server running?');
      }
    }

    tryAuth(5);
    return () => { cancelled = true; };
  }, [isAuthenticated, signIn, signUp]);

  if (error) {
    return (
      <div className="w-screen h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center space-y-2">
          <div className="text-2xl">⚠️</div>
          <p className="text-sm text-red-500 max-w-xs">{error}</p>
        </div>
      </div>
    );
  }

  if (!ready) {
    return (
      <div className="w-screen h-screen flex items-center justify-center bg-gray-50">
        <div className="flex items-center gap-3 text-gray-400">
          <div className="w-5 h-5 border-2 border-gray-300 border-t-brand-500 rounded-full animate-spin" />
          <span className="text-sm">Loading...</span>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
